import { useEnvironment } from '@wix/yoshi-flow-editor';

import { blockedMemberId } from '../../../../constants';
import {
  useAppSettings,
  useBuilderState,
  useMember,
} from '../../../../contexts/widgets';
import type { ControllerProps } from '../../../../types';
import {
  BuilderState,
  SettingsSection,
  SettingsSectionState,
  SettingsTab,
} from '../../../../types';

interface UseEmptyStateRendererOptions {
  shouldRenderDescription: boolean;
  shouldRenderBadges: boolean;
  shouldRenderOverview: boolean;
  shouldRenderBlogPosts: boolean;
}

type ShouldForceEmptyStateProps = ControllerProps['appSettings'] & {
  isMobile: boolean;
  builderState?: BuilderState;
};

const shouldForceEmptyState = ({
  tabOpened,
  sectionOpened,
  sectionState,
  isMobile,
  builderState,
}: ShouldForceEmptyStateProps) => {
  if (builderState === BuilderState.NoMemberProfile) {
    return true;
  }

  if (sectionState !== SettingsSectionState.Empty) {
    return false;
  }

  return (
    (tabOpened === SettingsTab.Sections &&
      sectionOpened === SettingsSection.About) ||
    (tabOpened === SettingsTab.Design &&
      sectionOpened === SettingsSection.TextStyle) ||
    (isMobile && tabOpened === SettingsTab.Design)
  );
};

export const useEmptyStateRenderer = ({
  shouldRenderDescription,
  shouldRenderBadges,
  shouldRenderOverview,
  shouldRenderBlogPosts,
}: UseEmptyStateRendererOptions) => {
  const { isMobile } = useEnvironment();
  const appSettings = useAppSettings();
  const builderState = useBuilderState();

  if (shouldForceEmptyState({ ...appSettings, isMobile, builderState })) {
    return { shouldRenderEmptyState: true };
  }

  return {
    shouldRenderEmptyState:
      !shouldRenderDescription &&
      !shouldRenderBadges &&
      !shouldRenderOverview &&
      !shouldRenderBlogPosts,
  };
};

export const useBlockedEmptyStateRenderer = () => {
  const { id } = useMember();
  return { shouldRenderBlockedEmptyState: id === blockedMemberId };
};
